package top.wcpe.allincore.ui.web.docs

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import top.wcpe.allincore.ui.web.Application
import top.wcpe.allincore.ui.web.DomWebRenderHost
import top.wcpe.allincore.ui.web.ModelCompositionHost
import top.wcpe.allincore.ui.web.PixiWebRenderApplication
import top.wcpe.allincore.ui.web.WebRenderSurface
import top.wcpe.allincore.ui.web.bootstrapWebPreview
import top.wcpe.allincore.ui.web.preview.PreviewApp
import top.wcpe.allincore.ui.web.preview.PreviewBackground
import top.wcpe.allincore.ui.web.preview.PreviewCandidateSource
import top.wcpe.allincore.ui.web.preview.PreviewSourceRuntime
import top.wcpe.allincore.ui.web.preview.DirectorySelection

/**
 * FR-UI-069 组件文档站壳：hash 路由 + 搜索/分类 + 源码/props + PreviewApp 嵌入预览。
 * 不复制编译/runtime/layout/render/mock，只调度 PreviewApp。
 */
internal class DocsApp(
    private val host: HTMLElement,
    private val manifestLoader: DocsManifestLoader = DocsManifestLoader(),
    private val previewWidth: Int = 400,
    private val previewHeight: Int = 280,
) {
    private var manifest: DocsManifest? = null
    private var filtered: List<DocsComponentEntry> = emptyList()
    private var query: String = ""
    private var activeId: String? = null
    private var activeExampleId: String? = null
    private var activeTheme: String? = null
    private var previewApp: PreviewApp? = null
    private var previewBackground: PreviewBackground? = null
    private var previewSurface: WebRenderSurface? = null
    private var disposed = false

    private lateinit var searchInput: HTMLInputElement
    private lateinit var navEl: HTMLElement
    private lateinit var mainEl: HTMLElement

    fun start() {
        document.body?.classList?.add("docs-mode")
        ensureDocsStylesheet()
        host.innerHTML = ""
        host.id = "docs-root"
        host.appendChild(buildHeader())
        navEl = document.createElement("nav").unsafeCast<HTMLElement>().also {
            it.id = "docs-nav"
            it.setAttribute("aria-label", "组件导航")
            host.appendChild(it)
        }
        mainEl = document.createElement("main").unsafeCast<HTMLElement>().also {
            it.id = "docs-main"
            host.appendChild(it)
        }
        mainEl.textContent = "正在加载组件文档清单…"
        manifestLoader.load().then { result ->
            if (disposed) return@then
            when (result) {
                is DocsManifestLoadResult.Ok -> {
                    manifest = result.manifest
                    filtered = result.manifest.components
                    bindHashRouting()
                    renderAll()
                }
                is DocsManifestLoadResult.Error -> {
                    mainEl.innerHTML = ""
                    val err = document.createElement("div")
                    err.className = "docs-error"
                    err.textContent = result.message
                    mainEl.appendChild(err)
                }
            }
        }
    }

    fun dispose() {
        if (disposed) return
        disposed = true
        window.removeEventListener("hashchange", hashListener)
        disposePreview()
        host.innerHTML = ""
        document.body?.classList?.remove("docs-mode")
    }

    private val hashListener: (Event) -> Unit = { renderAll() }

    private fun bindHashRouting() {
        window.addEventListener("hashchange", hashListener)
    }

    private fun buildHeader(): Element {
        val header = document.createElement("header")
        header.id = "docs-header"
        val title = document.createElement("h1")
        title.textContent = "AllinCore UI 组件文档站"
        header.appendChild(title)
        // 入口卡片：跳转到浏览器 UI 博物馆（真实 wiki/index 预览）
        val museumLink = document.createElement("a").unsafeCast<HTMLElement>()
        museumLink.id = "docs-museum-link"
        museumLink.setAttribute("href", "?mode=museum&w=960&h=640")
        museumLink.textContent = "打开 UI 博物馆"
        museumLink.setAttribute("aria-label", "打开浏览器 UI 博物馆")
        museumLink.style.asDynamic().marginLeft = "12px"
        museumLink.style.asDynamic().color = "#69f0ae"
        header.appendChild(museumLink)
        searchInput = document.createElement("input").unsafeCast<HTMLInputElement>()
        searchInput.id = "docs-search"
        searchInput.type = "search"
        searchInput.placeholder = "搜索组件名称 / id / 分类"
        searchInput.setAttribute("aria-label", "搜索组件")
        searchInput.addEventListener("input", {
            query = searchInput.value.trim().lowercase()
            applyFilter()
            renderNav()
        })
        searchInput.addEventListener("keydown", { event ->
            val keyboard = event.unsafeCast<KeyboardEvent>()
            if (keyboard.key == "Escape") {
                searchInput.value = ""
                query = ""
                applyFilter()
                renderNav()
            }
        })
        header.appendChild(searchInput)
        return header
    }

    private fun applyFilter() {
        val all = manifest?.components.orEmpty()
        filtered = if (query.isEmpty()) {
            all
        } else {
            all.filter { entry ->
                entry.id.contains(query) ||
                    entry.displayName.lowercase().contains(query) ||
                    entry.category.lowercase().contains(query)
            }
        }
    }

    private fun renderAll() {
        val current = manifest ?: return
        val route = parseDocsRoute(window.location.hash)
        when (route) {
            DocsRoute.Home -> {
                activeId = filtered.firstOrNull()?.id ?: current.components.firstOrNull()?.id
                if (activeId != null && window.location.hash.isBlank()) {
                    window.location.hash = encodeDocsComponentHash(activeId!!)
                    return
                }
                ensureActiveSelections(current.components.firstOrNull { it.id == activeId })
            }
            is DocsRoute.Component -> {
                val entry = current.components.firstOrNull { it.id == route.id }
                if (entry == null) {
                    activeId = null
                    activeExampleId = null
                    activeTheme = null
                    renderNav()
                    renderMain(DocsRoute.NotFound(route.id))
                    return
                }
                activeId = entry.id
                ensureActiveSelections(entry)
            }
            is DocsRoute.NotFound -> {
                activeId = null
                activeExampleId = null
                activeTheme = null
            }
        }
        renderNav()
        renderMain(route)
    }

    private fun ensureActiveSelections(entry: DocsComponentEntry?) {
        if (entry == null) {
            activeExampleId = null
            activeTheme = null
            return
        }
        if (activeExampleId == null || entry.examples.none { it.id == activeExampleId }) {
            activeExampleId = entry.examples.first().id
        }
        if (activeTheme == null || (entry.themes.isNotEmpty() && activeTheme !in entry.themes)) {
            activeTheme = entry.themes.firstOrNull()
        }
    }

    private fun renderNav() {
        navEl.innerHTML = ""
        if (filtered.isEmpty()) {
            val empty = document.createElement("div")
            empty.className = "docs-empty"
            empty.textContent = if (query.isEmpty()) "暂无组件条目" else "无匹配组件"
            navEl.appendChild(empty)
            return
        }
        filtered.groupBy { it.category }.forEach { (category, entries) ->
            val cat = document.createElement("div")
            cat.className = "docs-category"
            cat.textContent = category
            navEl.appendChild(cat)
            entries.forEach { entry ->
                val button = document.createElement("button").unsafeCast<HTMLButtonElement>()
                button.className = "docs-nav-item" + if (entry.id == activeId) " active" else ""
                button.type = "button"
                button.textContent = entry.displayName
                button.setAttribute("data-id", entry.id)
                button.onclick = {
                    window.location.hash = encodeDocsComponentHash(entry.id)
                }
                navEl.appendChild(button)
            }
        }
    }

    private fun renderMain(route: DocsRoute) {
        disposePreview()
        mainEl.innerHTML = ""
        when (route) {
            is DocsRoute.NotFound -> {
                val err = document.createElement("div")
                err.className = "docs-error"
                // 纯文本赋值，避免 raw hash 注入
                err.textContent = "未找到组件：${route.raw}"
                mainEl.appendChild(err)
                return
            }
            else -> Unit
        }
        val entry = manifest?.components?.firstOrNull { it.id == activeId }
        if (entry == null) {
            val empty = document.createElement("div")
            empty.className = "docs-empty"
            empty.textContent = "选择左侧组件查看详情"
            mainEl.appendChild(empty)
            return
        }
        val detail = document.createElement("div")
        detail.className = "docs-detail"
        detail.appendChild(buildOverviewPanel(entry))
        detail.appendChild(buildPropsPanel(entry))
        detail.appendChild(buildPreviewPanel(entry))
        detail.appendChild(buildSourcePanel(entry))
        mainEl.appendChild(detail)
        mountPreview(entry)
    }

    private fun buildOverviewPanel(entry: DocsComponentEntry): Element {
        val panel = document.createElement("section")
        panel.className = "docs-panel"
        val h2 = document.createElement("h2")
        h2.textContent = entry.displayName
        panel.appendChild(h2)
        val meta = document.createElement("div")
        meta.className = "docs-meta"
        meta.textContent =
            "id=${entry.id} · 分类=${entry.category} · 主题=${entry.themes.joinToString(",").ifEmpty { "—" }}"
        panel.appendChild(meta)
        val chips = document.createElement("div")
        chips.className = "docs-chip-row"
        listOf(
            "Web:${entry.capabilities.web}",
            "JCEF:${entry.capabilities.jcef}",
            "Fabric:${entry.capabilities.fabric}",
        ).forEach { label ->
            val chip = document.createElement("span")
            chip.className = "docs-chip"
            chip.textContent = label
            chips.appendChild(chip)
        }
        panel.appendChild(chips)
        if (entry.limits.isNotEmpty()) {
            val limits = document.createElement("div")
            limits.className = "docs-limits"
            limits.textContent = entry.limits.joinToString("；")
            panel.appendChild(limits)
        }
        return panel
    }

    private fun buildPropsPanel(entry: DocsComponentEntry): Element {
        val panel = document.createElement("section")
        panel.className = "docs-panel"
        val h3 = document.createElement("h3")
        h3.textContent = "Props"
        panel.appendChild(h3)
        if (entry.props.isEmpty()) {
            val empty = document.createElement("div")
            empty.className = "docs-empty"
            empty.textContent = "无 props"
            panel.appendChild(empty)
            return panel
        }
        val table = document.createElement("table")
        table.className = "docs-table"
        val thead = document.createElement("thead")
        thead.innerHTML = "<tr><th>名称</th><th>类型</th><th>必填</th><th>默认</th><th>说明</th></tr>"
        table.appendChild(thead)
        val tbody = document.createElement("tbody")
        entry.props.forEach { prop ->
            val tr = document.createElement("tr")
            tr.innerHTML =
                "<td>${escapeHtml(prop.name)}</td>" +
                    "<td>${escapeHtml(prop.type)}</td>" +
                    "<td>${if (prop.required) "是" else "否"}</td>" +
                    "<td>${escapeHtml(prop.defaultValue ?: "—")}</td>" +
                    "<td>${escapeHtml(prop.description)}</td>"
            tbody.appendChild(tr)
        }
        table.appendChild(tbody)
        panel.appendChild(table)
        return panel
    }

    private fun buildPreviewPanel(entry: DocsComponentEntry): Element {
        val panel = document.createElement("section")
        panel.className = "docs-panel"
        val h3 = document.createElement("h3")
        val example = entry.examples.firstOrNull { it.id == activeExampleId } ?: entry.examples.first()
        h3.textContent = "交互示例：${example.title}"
        panel.appendChild(h3)

        if (entry.examples.size > 1) {
            val exampleRow = document.createElement("div")
            exampleRow.className = "docs-switch-row"
            exampleRow.setAttribute("aria-label", "示例切换")
            entry.examples.forEach { item ->
                val button = document.createElement("button").unsafeCast<HTMLButtonElement>()
                button.type = "button"
                button.className = "docs-switch-btn" + if (item.id == example.id) " active" else ""
                button.textContent = item.title
                button.onclick = {
                    activeExampleId = item.id
                    renderAll()
                }
                exampleRow.appendChild(button)
            }
            panel.appendChild(exampleRow)
        }

        if (entry.themes.isNotEmpty()) {
            val themeRow = document.createElement("div")
            themeRow.className = "docs-switch-row"
            themeRow.setAttribute("aria-label", "主题切换")
            entry.themes.forEach { theme ->
                val button = document.createElement("button").unsafeCast<HTMLButtonElement>()
                button.type = "button"
                button.className = "docs-switch-btn" + if (theme == activeTheme) " active" else ""
                button.textContent = theme
                button.onclick = {
                    activeTheme = theme
                    renderAll()
                }
                themeRow.appendChild(button)
            }
            val note = document.createElement("div")
            note.className = "docs-theme-note"
            note.textContent = "当前主题：$activeTheme（示例内置主题语义；切换用于文档对照）"
            panel.appendChild(themeRow)
            panel.appendChild(note)
        }

        val host = document.createElement("div")
        host.id = "docs-preview-host"
        host.setAttribute("aria-label", "组件交互预览")
        panel.appendChild(host)
        return panel
    }

    private fun buildSourcePanel(entry: DocsComponentEntry): Element {
        val panel = document.createElement("section")
        panel.className = "docs-panel"
        val h3 = document.createElement("h3")
        h3.textContent = "组件源码"
        panel.appendChild(h3)
        val pre = document.createElement("pre")
        pre.className = "docs-source"
        pre.textContent = "加载中…"
        panel.appendChild(pre)
        manifestLoader.loadText(entry.sourcePath).then { text ->
            if (!disposed && activeId == entry.id) pre.textContent = text
        }.catch { reason ->
            if (!disposed && activeId == entry.id) {
                pre.textContent = "源码加载失败：${reason.toString()}"
            }
        }
        return panel
    }

    private fun mountPreview(entry: DocsComponentEntry) {
        val host = document.getElementById("docs-preview-host") as? HTMLElement ?: return
        val example = entry.examples.firstOrNull { it.id == activeExampleId } ?: entry.examples.first()
        val app = Application()
        val dependencies = createDocsPreviewDependencies()
        val surface = WebRenderSurface(
            "docs-preview",
            DomWebRenderHost(host),
            applicationFactory = { PixiWebRenderApplication(app) },
            initialWidth = previewWidth,
            initialHeight = previewHeight,
            modelCompositionHost = dependencies.modelCompositionHost,
        )
        previewSurface = surface
        bootstrapWebPreview(surface) {
            if (disposed || activeId != entry.id) {
                surface.dispose()
                return@bootstrapWebPreview
            }
            val background = PreviewBackground(surface, previewWidth, previewHeight)
            val preview = PreviewApp(
                app,
                previewWidth,
                previewHeight,
                background,
                dependencies.candidateSource,
                surface,
                dependencies.directoryRuntimeFactory,
            )
            previewBackground = background
            previewApp = preview
            // 主题名写入 data 属性，便于文档对照与后续 preview 主题注入
            host.setAttribute("data-docs-theme", activeTheme ?: "")
            host.setAttribute("data-docs-example", example.id)
            preview.start(example.entry)
        }
    }

    private fun disposePreview() {
        runCatching { previewApp?.dispose() }
        runCatching { previewBackground?.dispose() }
        runCatching { previewSurface?.dispose() }
        previewApp = null
        previewBackground = null
        previewSurface = null
    }
}

/** 由 WebPreviewMain 注入，避免 docs 包反向依赖私有工厂细节。 */
internal data class DocsPreviewDependencies(
    val candidateSource: PreviewCandidateSource,
    val modelCompositionHost: ModelCompositionHost,
    val directoryRuntimeFactory: ((DirectorySelection) -> PreviewSourceRuntime)?,
)

internal var docsPreviewDependenciesFactory: (() -> DocsPreviewDependencies)? = null

private fun createDocsPreviewDependencies(): DocsPreviewDependencies =
    docsPreviewDependenciesFactory?.invoke()
        ?: error("docsPreviewDependenciesFactory 未注入")

private fun escapeHtml(value: String): String = buildString(value.length) {
    value.forEach { ch ->
        when (ch) {
            '&' -> append("&amp;")
            '<' -> append("&lt;")
            '>' -> append("&gt;")
            '"' -> append("&quot;")
            '\'' -> append("&#39;")
            else -> append(ch)
        }
    }
}

private fun ensureDocsStylesheet() {
    if (document.getElementById("docs-shell-css") != null) return
    val link = document.createElement("link")
    link.id = "docs-shell-css"
    link.asDynamic().rel = "stylesheet"
    link.asDynamic().href = "docs-shell/docs-shell.css"
    document.head?.appendChild(link)
}
